"use client";

import React from "react";
import { badgeVariants } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ContributionTypeBadge } from "./contribution-type-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Star, GitCommit } from "lucide-react";
import { ExternalLink } from "@/components/ui/external-link";
import {
  type ContributionGroup,
  type ContributionRow,
} from "@/util/contributions";
import { formatStars } from "@/util/formatters";
import { trimSearchQuery } from "@/util/search-query";

/**
 * Renders text with backtick-wrapped segments as inline code.
 * e.g., "Fixed the `container_exists` helper" renders with code styling.
 */
function formatDescriptionWithCode(text: string): React.ReactNode {
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((part, index) => {
    if (part.startsWith("`") && part.endsWith("`")) {
      const codeContent = part.slice(1, -1);
      return (
        <code
          key={index}
          className="border-border bg-muted text-foreground rounded-md border px-1.5 py-0.5 font-mono text-base"
        >
          {codeContent}
        </code>
      );
    }
    return part;
  });
}

/**
 * Shared component for rendering the details of a contribution row.
 */
const ContributionRowDetails = React.memo(function ContributionRowDetails({
  row,
  showRepoName,
  searchQuery,
}: {
  row: ContributionRow;
  showRepoName: boolean;
  searchQuery: string;
}) {
  const normalizedSearchQuery = trimSearchQuery(searchQuery).toLowerCase();

  return (
    <div className="space-y-3">
      {showRepoName && (
        <div className="flex items-center gap-2">
          <ExternalLink
            href={row.repositoryUrl}
            className="text-muted-foreground hover:text-foreground text-base font-medium hover:underline"
          >
            {row.repositoryName}
          </ExternalLink>
          <div className="text-muted-foreground flex items-center gap-0.5 text-base">
            <Star
              className="size-3 fill-yellow-400 text-yellow-400"
              aria-hidden="true"
            />
            {formatStars(row.stars)}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {row.contribution.types.map((type) => (
          <ContributionTypeBadge key={type} type={type} className="text-base" />
        ))}
      </div>
      <p className="text-muted-foreground text-base/relaxed wrap-break-word">
        {formatDescriptionWithCode(row.contribution.description)}
      </p>
      <div className="flex flex-wrap gap-2">
        {row.contribution.commits.map((commit) => {
          const titleMatches =
            normalizedSearchQuery.length > 0 &&
            commit.title.toLowerCase().includes(normalizedSearchQuery);

          return (
            <React.Fragment key={commit.sha}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <ExternalLink
                    href={commit.url}
                    className="border-border text-muted-foreground hover:bg-muted hover:text-foreground inline-flex items-center gap-1 rounded-md border bg-transparent px-2 py-1 font-mono text-base transition-colors"
                  >
                    <GitCommit className="size-3" aria-hidden="true" />
                    {commit.sha.substring(0, 7)}
                    <span className="sr-only">: {commit.title}</span>
                  </ExternalLink>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="font-medium">{commit.title}</p>
                </TooltipContent>
              </Tooltip>
              {titleMatches && (
                <span
                  className="text-muted-foreground text-base wrap-break-word"
                  data-testid="matching-commit-title"
                >
                  {commit.title}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
});

ContributionRowDetails.displayName = "ContributionRowDetails";

/**
 * Shared component for rendering all contribution details for a group.
 */
export const ContributionGroupDetails = React.memo(
  function ContributionGroupDetails({
    group,
    className,
    id,
    searchQuery = "",
  }: {
    group: ContributionGroup;
    className?: string;
    id?: string;
    searchQuery?: string;
  }) {
    return (
      <div className={className} id={id}>
        {group.allRows.map((row) => (
          <div
            key={`${row.repositoryId}-${row.contribution.commits[0].sha}`}
            className="dark:border-border/40 space-y-3 border-t border-[rgba(0,0,0,0.08)] pt-4 first:border-t-0 first:pt-0"
          >
            <ContributionRowDetails
              row={row}
              showRepoName={row.repositoryId !== group.primaryRow.repositoryId}
              searchQuery={searchQuery}
            />
          </div>
        ))}
      </div>
    );
  },
);

ContributionGroupDetails.displayName = "ContributionGroupDetails";

// The exact markup <Badge variant="secondary" className=…> renders, as a plain
// span: a table of 57 rows with several technologies each hydrated two
// component fibers per label for output that never varies.
const TECHNOLOGY_BADGE_CLASS = cn(
  badgeVariants({ variant: "secondary" }),
  "dark:bg-secondary bg-[rgba(0,0,0,0.05)] text-base",
);

export const ContributionTechnologyBadges = React.memo(
  function ContributionTechnologyBadges({
    technologies,
  }: {
    technologies: string[];
  }) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {technologies.map((technology) => (
          <span
            key={technology}
            data-slot="badge"
            data-variant="secondary"
            className={TECHNOLOGY_BADGE_CLASS}
            data-testid="contribution-tech-badge"
          >
            {technology}
          </span>
        ))}
      </div>
    );
  },
);

ContributionTechnologyBadges.displayName = "ContributionTechnologyBadges";
